import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { axiosInstance } from "@/lib/api";
import { queue } from "@/components/Toast";
import { reconcileAllPlans } from "./calendarCache";
import { livePoll, useCalendarLive } from "./calendarLive";
import type { CalendarPlatform } from "@/types/contentCalendar.type";
import type { DistributionLiveBlock } from "../live/liveTypes";
import {
  distributionFiles,
  distributionPublications,
  type ChannelCopy,
  type DeliverableKind,
  type DeliverableThumbnailTicket,
  type DeliverableUploadTicket,
  type DistributionResult,
  type Publication,
  type PublishableAccount,
  type SlotDeliverable,
  type PublicationFields,
  type SlotFile,
  type SlotItem,
  type UpdatePublicationInput,
} from "@/types/scheduling.type";

/**
 * The scheduling surface: deliverable upload + per-platform publications for
 * one slot. Mutations refresh both the detailed distribution read and the
 * calendar summaries that mirror its state.
 */

export const distributionKey = (slotId: string | null) => ["distribution", slotId];

/**
 * Every write on one post's distribution carries this key prefix, so the read
 * and the writes can see each other (see readGuard and settleWrite).
 */
export const distributionWriteKey = (slotId: string | null, kind?: string) =>
  kind ? ["distributionWrite", slotId, kind] : ["distributionWrite", slotId];

/**
 * True when a write on the post is still running, or started after `since`.
 * A read that overlaps a write may carry the state from before it, and
 * writing that over the optimistic state is what made the page jump back and
 * forth. Such a read keeps the cache; the write's own settle refreshes it.
 */
function wroteSince(qc: QueryClient, slotId: string | null, since: number): boolean {
  return qc
    .getMutationCache()
    .findAll({ mutationKey: distributionWriteKey(slotId) })
    .some((mutation) => mutation.state.status === "pending" || mutation.state.submittedAt >= since);
}

/**
 * After a write's reply: unless another write on this post is still running
 * (its optimistic state must not be overwritten by this reply), take the
 * server's answer and refresh the reads. The last write to settle does it.
 */
function settleWrite(qc: QueryClient, slotId: string | null, payload?: DistributionResult | null) {
  if (qc.isMutating({ mutationKey: distributionWriteKey(slotId) }) > 1) return;
  if (payload) qc.setQueryData(distributionKey(slotId), payload);
  invalidateSchedulingReads(qc, slotId);
}
export const publishableAccountsKey = (brandId: string | null) => [
  "publishableAccounts",
  brandId,
];

/**
 * Scheduling state is duplicated only as a lightweight summary on the open
 * month and months index. Refresh all three reads together so a publish action
 * cannot leave the panel saying "Live" while the calendar still says "Ready".
 * Prefix invalidation is deliberate: these hooks do not receive plan/brand ids,
 * and React Query limits the refresh to calendar reads already in cache.
 */
function invalidateSchedulingReads(qc: QueryClient, slotId: string | null) {
  void qc.invalidateQueries({ queryKey: distributionKey(slotId) });
  // Through the month's write gate: a month with a write in flight refreshes when it settles.
  reconcileAllPlans(qc);
  void qc.invalidateQueries({ queryKey: ["calendarPlans"] });
}

function errorMessage(err: unknown, fallback: string): string {
  if (isAxiosError(err)) {
    return (err.response?.data as { message?: string })?.message ?? err.message ?? fallback;
  }
  return fallback;
}

function toastError(err: unknown, fallback: string) {
  queue.add({ title: fallback, description: errorMessage(err, fallback), variant: "error" });
}

/** Write one publication's change wherever the read carries it. */
function patchPublicationInCache(
  qc: QueryClient,
  slotId: string | null,
  publicationId: string,
  patch: Partial<Publication>,
) {
  const apply = (publications: Publication[]) =>
    publications.map((publication) => (publication.id === publicationId ? { ...publication, ...patch } : publication));
  qc.setQueryData<DistributionResult>(distributionKey(slotId), (data) =>
    data?.deliverable
      ? { ...data, deliverable: { ...data.deliverable, publications: apply(data.deliverable.publications) } }
      : data,
  );
}

/** The read with the Live tab's block (live/liveTypes.ts). */
type DistributionWithLive = DistributionResult & { live?: DistributionLiveBlock | null };
type LivePostEntry = NonNullable<DistributionLiveBlock["posts"]>[number];

/** While a live post's numbers are read on their own (hourly, then daily), the panel checks this often. */
const LIVE_NUMBERS_POLL_MS = 5 * 60 * 1000;

function livePostsOf(data: DistributionWithLive | null | undefined): LivePostEntry[] {
  return data?.live?.posts ?? [];
}

/** Write one channel's live post wherever the read carries it; null takes it out. */
function patchLivePostInCache(qc: QueryClient, slotId: string | null, channelId: string, next: LivePostEntry | null) {
  qc.setQueryData<DistributionWithLive>(distributionKey(slotId), (data) => {
    if (!data) return data;
    const posts = livePostsOf(data).filter((post) => post.channelId !== channelId);
    return { ...data, live: { ...(data.live ?? {}), posts: next ? [...posts, next] : posts } };
  });
}

function livePostInCache(qc: QueryClient, slotId: string | null, channelId: string): LivePostEntry | null {
  return livePostsOf(qc.getQueryData<DistributionWithLive>(distributionKey(slotId))).find((post) => post.channelId === channelId) ?? null;
}

/** Files + publications + skipped channels for one slot. Polls while a file is checked. */
export function useDistribution(slotId: string | null, enabled = true) {
  const qc = useQueryClient();
  const live = useCalendarLive();
  return useQuery({
    queryKey: distributionKey(slotId),
    queryFn: async () => {
      const started = Date.now();
      const res = await axiosInstance.get<DistributionWithLive>(
        `/scheduling/slots/${slotId}/distribution`,
      );
      // A write overlapped this read: keep what the page shows (the write's
      // settle reads again once it is done).
      const cached = qc.getQueryData<DistributionWithLive>(distributionKey(slotId));
      if (cached && wroteSince(qc, slotId, started)) return cached;
      return res.data;
    },
    enabled: !!slotId && enabled,
    refetchInterval: (query) => livePoll(live, distributionPollMs(query.state.data)),
    refetchOnWindowFocus: !live,
  });
}

/**
 * The post's distribution poll without the socket: fast while a file is
 * checked, a number is read or a publication moves; slow while numbers are
 * still on a schedule; off otherwise.
 */
function distributionPollMs(data: DistributionWithLive | undefined): number | false {
  const checking = distributionFiles(data).some(
    (file) => file.status === "UPLOADING" || file.status === "PROCESSING",
  );
  if (checking) return 3000;
  // A live post's numbers being read right now land in seconds.
  if (livePostsOf(data).some((post) => post.tracking?.reading)) return 3000;
  const pubs = distributionPublications(data);
  const nonTerminal = pubs.some(
    (p) =>
      ["DRAFT", "READY", "SCHEDULED", "PUBLISHING"].includes(p.status) ||
      // FAILED is only terminal when the dispatcher did not set another
      // attempt. While a backoff timestamp exists, keep watching so the UI
      // moves to Publishing/Live without requiring a focus change.
      (p.status === "FAILED" && Boolean(p.nextAttemptAt)),
  );
  if (nonTerminal) return 3000;
  // Numbers still read on a schedule: slow, never off.
  return livePostsOf(data).some((post) => post.tracking?.nextReadAt) ? LIVE_NUMBERS_POLL_MS : false;
}

/** Connected publishable accounts for a brand, with scope-sufficiency flags. */
export function usePublishableAccounts(brandId: string | null, enabled = true) {
  return useQuery({
    queryKey: publishableAccountsKey(brandId),
    queryFn: async () => {
      const res = await axiosInstance.get<{ accounts: PublishableAccount[] }>(
        `/scheduling/brands/${brandId}/publishable-accounts`,
      );
      return res.data.accounts;
    },
    enabled: !!brandId && enabled,
    // Connection health (canPublish / NEEDS_REAUTH) changes server-side via the
    // refresh crons; keep the Schedule gate current while a panel is open.
    refetchInterval: 30000,
  });
}

/** Step 1 of the upload: presigned PUT ticket. The actual PUT is putToPresignedUrl. */
export function useDeliverableUploadUrl(slotId: string | null) {
  return useMutation({
    mutationFn: async (file: File) => {
      const res = await axiosInstance.post<DeliverableUploadTicket>(
        `/scheduling/slots/${slotId}/deliverable/upload-url`,
        {
          filename: file.name,
          mimetype: file.type,
          extension: file.name.split(".").pop() ?? "",
          size: file.size,
        },
      );
      return res.data;
    },
  });
}

/** Presigned PUT ticket for a video cover image. */
export function useDeliverableThumbnailUploadUrl(slotId: string | null) {
  return useMutation({
    mutationFn: async (input: { mimetype: string; extension: string; size: number }) => {
      const res = await axiosInstance.post<DeliverableThumbnailTicket>(
        `/scheduling/slots/${slotId}/deliverable/thumbnail/upload-url`,
        input,
      );
      return res.data;
    },
  });
}

export interface ConfirmDeliverableInput {
  uploadToken: string;
  thumbnailToken?: string;
  /** One of the post's platforms; omitted or null for every platform. */
  forPlatform?: CalendarPlatform | null;
  /** The carousel slide the picture was dropped on; omitted fills the first empty one. */
  itemId?: string;
  /** What was uploaded, for the row the post shows at once. Never sent. */
  file?: { name: string; type: string; size: number; kind: DeliverableKind };
}

let pendingFileSeq = 0;

/** The row a confirm shows before the server has the file: it has no server id yet. */
export function isPendingSlotFile(file: Pick<SlotFile, "id">): boolean {
  return file.id.startsWith("pending:");
}

/**
 * Step 2: confirm the uploaded file. It always adds a file to the post; once
 * the probe passes, the server makes an every-platform file the default and
 * gives a per-platform file to its platform.
 */
export function useConfirmDeliverable(slotId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: distributionWriteKey(slotId, "confirm"),
    mutationFn: async (input: ConfirmDeliverableInput) => {
      const res = await axiosInstance.post<{ deliverable: SlotDeliverable }>(
        `/scheduling/slots/${slotId}/deliverable/confirm`,
        {
          uploadToken: input.uploadToken,
          ...(input.thumbnailToken ? { thumbnailToken: input.thumbnailToken } : {}),
          ...(input.forPlatform ? { forPlatform: input.forPlatform } : {}),
          ...(input.itemId ? { itemId: input.itemId } : {}),
        },
      );
      return res.data.deliverable;
    },
    // Optimistic: the file joins the post on confirm, as being checked, so the
    // page moves on at once; only this row leaves again if the server refuses.
    onMutate: async (input) => {
      if (!input.file || !qc.getQueryData(distributionKey(slotId))) return { pendingId: null };
      await qc.cancelQueries({ queryKey: distributionKey(slotId) });
      const now = new Date().toISOString();
      pendingFileSeq += 1;
      const pending: SlotFile = {
        id: `pending:${pendingFileSeq}`,
        slotId: slotId ?? "",
        kind: input.file.kind,
        status: "PROCESSING",
        fileName: input.file.name,
        mimeType: input.file.type,
        sizeBytes: input.file.size,
        width: null,
        height: null,
        durationSeconds: null,
        processingError: null,
        thumbnailKey: null,
        createdAt: now,
        updatedAt: now,
        mediaUrl: null,
        thumbnailUrl: null,
        forPlatform: input.forPlatform ?? null,
        isDefault: false,
      };
      qc.setQueryData<DistributionResult>(distributionKey(slotId), (data) =>
        data ? { ...data, files: [pending, ...distributionFiles(data)] } : data,
      );
      return { pendingId: pending.id };
    },
    onSuccess: (deliverable, input, context) => {
      if (!context?.pendingId) return;
      qc.setQueryData<DistributionResult>(distributionKey(slotId), (data) =>
        data
          ? {
              ...data,
              files: distributionFiles(data).map((file) =>
                file.id === context.pendingId ? { ...file, ...deliverable, forPlatform: input.forPlatform ?? null } : file,
              ),
            }
          : data,
      );
    },
    onError: (err, _input, context) => {
      if (context?.pendingId) {
        qc.setQueryData<DistributionResult>(distributionKey(slotId), (data) =>
          data ? { ...data, files: distributionFiles(data).filter((file) => file.id !== context.pendingId) } : data,
        );
      }
      toastError(err, "Could not start processing");
    },
    onSettled: () => settleWrite(qc, slotId),
  });
}

/** A video's cover; `deliverableId` names the file, else the default file. */
export function useSetDeliverableThumbnail(slotId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: distributionWriteKey(slotId, "thumbnail"),
    mutationFn: async (input: { thumbnailToken: string; deliverableId?: string }) => {
      const res = await axiosInstance.post<DistributionResult>(
        `/scheduling/slots/${slotId}/deliverable/thumbnail/confirm`,
        input,
      );
      return res.data;
    },
    onSettled: () => settleWrite(qc, slotId),
    onError: (err) => toastError(err, "Could not update the thumbnail"),
  });
}

/** Legacy: removes the default file. Prefer useDeleteSlotFile. */
export function useDeleteDeliverable(slotId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: distributionWriteKey(slotId, "deleteDefault"),
    mutationFn: async () => {
      await axiosInstance.delete(`/scheduling/slots/${slotId}/deliverable`);
    },
    onSettled: () => settleWrite(qc, slotId),
    onError: (err) => toastError(err, "Could not remove the asset"),
  });
}

export const deleteSlotFileKey = (slotId: string | null) => distributionWriteKey(slotId, "deleteFile");

/**
 * Remove one file from the post. The server refuses (409 FILE_IN_USE, with
 * the reason) while a scheduled or posted publication uses it; a 404 means it
 * is already gone.
 */
export function useDeleteSlotFile(slotId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: deleteSlotFileKey(slotId),
    mutationFn: async (deliverableId: string) => {
      const res = await axiosInstance.delete<DistributionResult>(
        `/scheduling/slots/${slotId}/files/${deliverableId}`,
      );
      return res.data;
    },
    // Optimistic: the file leaves the list on confirm; on a refusal only this
    // file comes back, where it was.
    onMutate: async (deliverableId) => {
      await qc.cancelQueries({ queryKey: distributionKey(slotId) });
      const data = qc.getQueryData<DistributionResult>(distributionKey(slotId));
      const files = distributionFiles(data);
      const index = files.findIndex((file) => file.id === deliverableId);
      if (!data || index < 0) return { removed: null };
      qc.setQueryData<DistributionResult>(distributionKey(slotId), {
        ...data,
        files: files.filter((file) => file.id !== deliverableId),
      });
      return { removed: { file: files[index], index } };
    },
    // Another write still running: its optimistic state must not come back from this reply.
    onSuccess: (payload) => settleWrite(qc, slotId, payload),
    onError: (err, _deliverableId, context) => {
      const removed = context?.removed;
      // 404: the file is not on the post any more, so it stays out of the list.
      const gone = isAxiosError(err) && err.response?.status === 404;
      if (removed && !gone) {
        qc.setQueryData<DistributionResult>(distributionKey(slotId), (data) => {
          if (!data) return data;
          const files = distributionFiles(data);
          if (files.some((file) => file.id === removed.file.id)) return data;
          const next = [...files];
          next.splice(Math.min(removed.index, next.length), 0, removed.file);
          return { ...data, files: next };
        });
      }
      toastError(err, "Could not remove the file");
    },
    onSettled: (_data, error) => {
      if (error) settleWrite(qc, slotId);
    },
  });
}

export function useUpdatePublication(slotId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: distributionWriteKey(slotId, "publication"),
    mutationFn: async (payload: { publicationId: string; input: UpdatePublicationInput }) => {
      const res = await axiosInstance.patch(`/scheduling/publications/${payload.publicationId}`, payload.input);
      return res.data;
    },
    // A platform's file shows at once; a refusal (409 FILE_REMOVED or
    // FILE_NOT_READY, 400 not editable) restores only that platform's, and the
    // toast carries the server's message.
    onMutate: async ({ publicationId, input }) => {
      if (input.deliverableId === undefined) return { restore: null };
      await qc.cancelQueries({ queryKey: distributionKey(slotId) });
      const before = distributionPublications(qc.getQueryData<DistributionResult>(distributionKey(slotId))).find(
        (publication) => publication.id === publicationId,
      );
      if (!before) return { restore: null };
      patchPublicationInCache(qc, slotId, publicationId, { deliverableId: input.deliverableId, fileChosen: true });
      return { restore: { deliverableId: before.deliverableId, fileChosen: before.fileChosen } };
    },
    onError: (err, { publicationId }, context) => {
      if (context?.restore) patchPublicationInCache(qc, slotId, publicationId, context.restore);
      toastError(err, "Could not save the publication");
    },
    onSettled: () => settleWrite(qc, slotId),
  });
}

/** What each action turns a publication into, shown at once. */
const ACTION_STATUS: Record<"schedule" | "unschedule" | "retry" | "publish-now", Publication["status"]> = {
  schedule: "SCHEDULED",
  unschedule: "READY",
  retry: "PUBLISHING",
  "publish-now": "SCHEDULED",
};

export const publicationActionKey = (slotId: string | null) => distributionWriteKey(slotId, "publicationAction");

/**
 * Schedule, unschedule, retry and publish-now, optimistic: the publication
 * takes its new status in the cache at once, a refusal restores only that
 * publication (with the server's reason), and the reads refresh once the last
 * action settles.
 */
function usePublicationAction(
  slotId: string | null,
  action: "schedule" | "unschedule" | "retry" | "publish-now",
  failureToast: string,
  { optimistic = true }: { optimistic?: boolean } = {},
) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: publicationActionKey(slotId),
    mutationFn: async (publicationId: string) => {
      const res = await axiosInstance.post(`/scheduling/publications/${publicationId}/${action}`);
      return res.data;
    },
    onMutate: async (publicationId) => {
      // Not optimistic: the caller shows its own loading state until the server answers.
      if (!optimistic) return { restore: null };
      await qc.cancelQueries({ queryKey: distributionKey(slotId) });
      const before = distributionPublications(qc.getQueryData<DistributionResult>(distributionKey(slotId))).find(
        (publication) => publication.id === publicationId,
      );
      if (!before) return { restore: null };
      patchPublicationInCache(qc, slotId, publicationId, { status: ACTION_STATUS[action], ...(action === "retry" ? { lastError: null } : {}) });
      return { restore: { status: before.status, lastError: before.lastError } };
    },
    onError: (err, publicationId, context) => {
      if (context?.restore) patchPublicationInCache(qc, slotId, publicationId, context.restore);
      toastError(err, failureToast);
    },
    onSettled: () => settleWrite(qc, slotId),
  });
}

export const useSchedulePublication = (slotId: string | null, options?: { optimistic?: boolean }) =>
  usePublicationAction(slotId, "schedule", "Could not schedule", options);
export const useUnschedulePublication = (slotId: string | null, options?: { optimistic?: boolean }) =>
  usePublicationAction(slotId, "unschedule", "Could not unschedule", options);
export const useRetryPublication = (slotId: string | null) =>
  usePublicationAction(slotId, "retry", "Could not retry");
export const usePublishNowPublication = (slotId: string | null, options?: { optimistic?: boolean }) =>
  usePublicationAction(slotId, "publish-now", "Could not post now", options);

export function useMaterializePublications(slotId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: distributionWriteKey(slotId, "materialize"),
    mutationFn: async () => {
      const res = await axiosInstance.post<DistributionResult>(
        `/scheduling/slots/${slotId}/publications/materialize`,
      );
      return res.data;
    },
    onSettled: () => settleWrite(qc, slotId),
    onError: (err) => toastError(err, "Could not create the publication"),
  });
}

// ---- Live posts: a channel's post link and its numbers (backend #139/#140) ----

export const livePostMutationKey = (slotId: string | null) => distributionWriteKey(slotId, "livePost");

/**
 * After the reply of one live post write, unless another one on this post is
 * still running: its optimistic entry must not be overwritten by this reply.
 */
function settleLivePost(qc: QueryClient, slotId: string | null, payload?: DistributionWithLive) {
  settleWrite(qc, slotId, payload);
}

/**
 * Paste the link to a channel's post. Not optimistic: the caller shows a
 * loading state until the server answers, which avoids races with the read;
 * a refusal (wrong platform's link, a channel CreativeOS posts itself) toasts.
 */
export function useSetLivePostLink(slotId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: livePostMutationKey(slotId),
    mutationFn: async (input: { platform: CalendarPlatform; url: string }) => {
      const res = await axiosInstance.put<DistributionWithLive>(
        `/scheduling/slots/${slotId}/live-posts/${input.platform}`,
        { url: input.url },
      );
      return res.data;
    },
    onSuccess: (payload) => settleLivePost(qc, slotId, payload),
    onError: (err) => {
      toastError(err, "Could not save the link");
      settleLivePost(qc, slotId);
    },
  });
}

/** Take a pasted link back, once the server has (no optimistic write). */
export function useClearLivePostLink(slotId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: livePostMutationKey(slotId),
    mutationFn: async (platform: CalendarPlatform) => {
      const res = await axiosInstance.delete<DistributionWithLive>(
        `/scheduling/slots/${slotId}/live-posts/${platform}`,
      );
      return res.data;
    },
    onSuccess: (payload) => settleLivePost(qc, slotId, payload),
    onError: (err) => {
      toastError(err, "Could not remove the link");
      settleLivePost(qc, slotId);
    },
  });
}

/** Read a live post's numbers now. Optimistic: the post shows the read running. */
export function useRefreshLivePost(slotId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: livePostMutationKey(slotId),
    mutationFn: async (platform: CalendarPlatform) => {
      const res = await axiosInstance.post<DistributionWithLive>(
        `/scheduling/slots/${slotId}/live-posts/${platform}/refresh`,
      );
      return res.data;
    },
    onMutate: async (platform) => {
      await qc.cancelQueries({ queryKey: distributionKey(slotId) });
      const before = livePostInCache(qc, slotId, platform);
      if (before?.tracking) {
        patchLivePostInCache(qc, slotId, platform, { ...before, tracking: { ...before.tracking, reading: true } });
      }
      return { before };
    },
    onSuccess: (payload) => settleLivePost(qc, slotId, payload),
    onError: (err, platform, context) => {
      if (context?.before) patchLivePostInCache(qc, slotId, platform, context.before);
      toastError(err, "Could not refresh the numbers");
    },
  });
}

/** One good read of a live post's numbers. A null number: the source couldn't give it. */
export interface LivePostHistoryPoint {
  readAt: string;
  source: "CONNECTED" | "PUBLIC" | null;
  views: number | null;
  likes: number | null;
  comments: number | null;
  shares: number | null;
  saves: number | null;
}

/**
 * Live posts' numbers over time, for the chart: one read per platform, keyed
 * on when its numbers were last read, so a new read refetches only that one.
 * A platform with no numbers yet is not asked.
 */
export function useLivePostHistories(
  slotId: string | null,
  entries: { platform: CalendarPlatform; metricsUpdatedAt: string | null | undefined }[],
) {
  return useQueries({
    queries: entries.map((entry) => ({
      queryKey: ["livePostHistory", slotId, entry.platform, entry.metricsUpdatedAt ?? null],
      queryFn: async () => {
        const res = await axiosInstance.get<{ points: LivePostHistoryPoint[] }>(
          `/scheduling/slots/${slotId}/live-posts/${entry.platform}/history`,
        );
        return res.data.points;
      },
      enabled: !!slotId && !!entry.metricsUpdatedAt,
      staleTime: Infinity,
    })),
  });
}

/** One post in the Schedule view: the post, and the same read its Live tab gets. */
export interface UpcomingPost {
  slot: {
    id: string;
    planId: string;
    title: string;
    ideaTitle: string | null;
    lengthSeconds: number | null;
    mediaType: string;
    orientation: string;
    aspectRatio: string;
    pillarName: string;
    scheduledAt: string;
    origin: "PIPELINE" | "DIRECT";
  };
  distribution: DistributionWithLive;
}

export interface UpcomingResult {
  timezone: string;
  from: string;
  to: string;
  days: number;
  posts: UpcomingPost[];
}

export const upcomingKey = (brandId: string | null) => ["upcomingDistribution", brandId];

/**
 * The calendar's Schedule view: every post going live in the next 14 days with
 * its distribution read. Polls while anything is about to post or retrying, like
 * the post's own read; otherwise slow, never off.
 */
export function useUpcomingDistribution(brandId: string | null, enabled = true) {
  const live = useCalendarLive();
  return useQuery({
    queryKey: upcomingKey(brandId),
    queryFn: async () => {
      const res = await axiosInstance.get<UpcomingResult>(`/scheduling/brands/${brandId}/upcoming`);
      return res.data;
    },
    enabled: !!brandId && enabled,
    refetchInterval: (query) => {
      const posting = (query.state.data?.posts ?? []).some((post) =>
        distributionPublications(post.distribution).some(
          (p) => p.status === "PUBLISHING" || (p.status === "FAILED" && Boolean(p.nextAttemptAt)),
        ),
      );
      return livePoll(live, posting ? 5000 : 60_000);
    },
    refetchOnWindowFocus: !live,
  });
}

/**
 * Schedule one channel from the Schedule view, optimistic: the row reads
 * Scheduled at once; a refusal puts it back with the server's reason.
 */
export function useScheduleUpcoming(brandId: string | null) {
  const qc = useQueryClient();
  const setStatus = (slotId: string, publicationId: string, status: Publication["status"]) =>
    qc.setQueryData<UpcomingResult>(upcomingKey(brandId), (data) =>
      data
        ? {
            ...data,
            posts: data.posts.map((post) => {
              if (post.slot.id !== slotId) return post;
              const apply = (list: Publication[] | undefined) =>
                list?.map((p) => (p.id === publicationId ? { ...p, status } : p));
              const distribution = post.distribution;
              return {
                ...post,
                distribution: {
                  ...distribution,
                  deliverable: distribution.deliverable
                    ? { ...distribution.deliverable, publications: apply(distribution.deliverable.publications) ?? [] }
                    : distribution.deliverable,
                },
              };
            }),
          }
        : data,
    );
  return useMutation({
    // Schedule a READY publication, or try a FAILED one again; both land on SCHEDULED.
    mutationFn: async ({
      publicationId,
      retry = false,
    }: {
      slotId: string;
      publicationId: string;
      previous: Publication["status"];
      retry?: boolean;
    }) => {
      const res = await axiosInstance.post(`/scheduling/publications/${publicationId}/${retry ? "retry" : "schedule"}`);
      return res.data;
    },
    onMutate: async ({ slotId, publicationId }) => {
      await qc.cancelQueries({ queryKey: upcomingKey(brandId) });
      setStatus(slotId, publicationId, "SCHEDULED");
    },
    onError: (err, { slotId, publicationId, previous, retry }) => {
      setStatus(slotId, publicationId, previous);
      toastError(err, retry ? "Could not try again" : "Could not schedule");
    },
    onSettled: (_data, _err, { slotId }) => {
      void qc.invalidateQueries({ queryKey: upcomingKey(brandId) });
      invalidateSchedulingReads(qc, slotId);
    },
  });
}

// ---- The post's copy and slides (docs/post-channels-slides-plan.md) ----

/** Write one platform's copy wherever the read carries it (the copy row, and its publication's fields). */
function patchChannelCopyInCache(qc: QueryClient, slotId: string | null, platform: CalendarPlatform, copy: PublicationFields) {
  qc.setQueryData<DistributionResult>(distributionKey(slotId), (data) => {
    if (!data) return data;
    const rows = data.channelCopy ?? [];
    const channelCopy: ChannelCopy[] = rows.some((row) => row.platform === platform)
      ? rows.map((row) => (row.platform === platform ? { ...row, copy } : row))
      : [...rows, { platform, copy, updatedAt: null, publishable: true }];
    const publications = distributionPublications(data).map((publication) =>
      publication.platform === platform ? { ...publication, fields: { ...publication.fields, ...copy } } : publication,
    );
    return {
      ...data,
      channelCopy,
      deliverable: data.deliverable ? { ...data.deliverable, publications } : data.deliverable,
    };
  });
}

function channelCopyInCache(qc: QueryClient, slotId: string | null, platform: CalendarPlatform): PublicationFields | null {
  const row = qc.getQueryData<DistributionResult>(distributionKey(slotId))?.channelCopy?.find((r) => r.platform === platform);
  return row ? row.copy : null;
}

const channelCopyKey = (slotId: string | null) => distributionWriteKey(slotId, "copy");

/**
 * One platform's copy (caption, YouTube's title and description, TikTok's
 * cover moment). Saved on the post, so it works before any file or account
 * exists and on platforms posted by hand. Rolls back only that platform.
 */
export function useUpdateChannelCopy(slotId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: channelCopyKey(slotId),
    mutationFn: async (input: { platform: CalendarPlatform; copy: PublicationFields }) => {
      const res = await axiosInstance.put<DistributionResult>(
        `/scheduling/slots/${slotId}/platforms/${input.platform}/copy`,
        { copy: input.copy },
      );
      return res.data;
    },
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: distributionKey(slotId) });
      const before = channelCopyInCache(qc, slotId, input.platform);
      patchChannelCopyInCache(qc, slotId, input.platform, input.copy);
      return { before };
    },
    onSuccess: (payload) => settleWrite(qc, slotId, payload),
    onError: (err, input, context) => {
      if (context?.before) patchChannelCopyInCache(qc, slotId, input.platform, context.before);
      toastError(err, "Could not save the copy");
    },
    onSettled: (_data, error) => {
      if (error) settleWrite(qc, slotId);
    },
  });
}

const slotItemsKey = (slotId: string | null) => distributionWriteKey(slotId, "slides");
let pendingSlideSeq = 0;

/** A slide made on this page that the server has not given an id yet. */
export function isPendingSlide(item: Pick<SlotItem, "id">): boolean {
  return item.id.startsWith("pending-slide:");
}

function setItemsInCache(qc: QueryClient, slotId: string | null, items: SlotItem[]) {
  qc.setQueryData<DistributionResult>(distributionKey(slotId), (data) => (data ? { ...data, items } : data));
}

function itemsInCache(qc: QueryClient, slotId: string | null): SlotItem[] {
  return qc.getQueryData<DistributionResult>(distributionKey(slotId))?.items ?? [];
}

/** A new slide's id, made here so its tile keeps one identity through the server's answer. */
export function newSlideId(): string {
  return crypto.randomUUID();
}

/**
 * The slide list after a change: the order, labels and pictures the page shows
 * at once, as the server will. A single picture becoming a carousel is slide 1.
 */
function nextItems(before: SlotItem[], entries: Array<{ id?: string | null; label: string }>, lead: string | null): SlotItem[] {
  if (entries.length < 2) return [];
  return entries.map((entry, index) => {
    const kept = entry.id ? before.find((item) => item.id === entry.id) : undefined;
    if (kept) return { ...kept, position: index + 1, label: entry.label };
    if (!entry.id) pendingSlideSeq += 1;
    return {
      id: entry.id ?? `pending-slide:${pendingSlideSeq}`,
      position: index + 1,
      kind: "IMAGE",
      label: entry.label,
      briefPanelId: null,
      deliverableId: index === 0 && before.length < 2 ? lead : null,
    };
  });
}

/**
 * The whole slide list, in order: add, remove, reorder and rename in one
 * call. Fewer than two slides make the post a single picture again.
 */
export function useSetSlotItems(slotId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: slotItemsKey(slotId),
    mutationFn: async (entries: Array<{ id?: string | null; label: string }>) => {
      const res = await axiosInstance.put<DistributionResult>(`/scheduling/slots/${slotId}/items`, {
        // A slide made on this page and not saved yet is new to the server too.
        items: entries.map((entry) => ({
          id: entry.id && !isPendingSlide({ id: entry.id }) ? entry.id : null,
          label: entry.label,
        })),
      });
      return res.data;
    },
    onMutate: async (entries) => {
      await qc.cancelQueries({ queryKey: distributionKey(slotId) });
      const before = itemsInCache(qc, slotId);
      // The picture the single-picture post shows (the server's display default).
      const lead =
        distributionFiles(qc.getQueryData<DistributionResult>(distributionKey(slotId))).find(
          (file) => file.isDefault && file.kind === "IMAGE",
        )?.id ?? null;
      setItemsInCache(qc, slotId, nextItems(before, entries, lead));
      return { before };
    },
    onSuccess: (payload) => settleWrite(qc, slotId, payload),
    onError: (err, _entries, context) => {
      if (context) setItemsInCache(qc, slotId, context.before);
      toastError(err, "Could not change the slides");
    },
    onSettled: (_data, error) => {
      if (error) settleWrite(qc, slotId);
    },
  });
}

/**
 * Put a picture on a slide, or clear it (null). A picture already on another
 * slide swaps places with what this slide held, as the server does.
 */
export function useSetSlotItemFile(slotId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: slotItemsKey(slotId),
    mutationFn: async (input: { itemId: string; deliverableId: string | null }) => {
      const res = await axiosInstance.put<DistributionResult>(
        `/scheduling/slots/${slotId}/items/${input.itemId}/file`,
        { deliverableId: input.deliverableId },
      );
      return res.data;
    },
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: distributionKey(slotId) });
      const before = itemsInCache(qc, slotId);
      const target = before.find((item) => item.id === input.itemId);
      setItemsInCache(
        qc,
        slotId,
        before.map((item) => {
          if (item.id === input.itemId) return { ...item, deliverableId: input.deliverableId };
          if (input.deliverableId && item.deliverableId === input.deliverableId) {
            return { ...item, deliverableId: target?.deliverableId ?? null };
          }
          return item;
        }),
      );
      return { before };
    },
    onSuccess: (payload) => settleWrite(qc, slotId, payload),
    onError: (err, _input, context) => {
      if (context) setItemsInCache(qc, slotId, context.before);
      toastError(err, "Could not place the picture");
    },
    onSettled: (_data, error) => {
      if (error) settleWrite(qc, slotId);
    },
  });
}
